<script lang="ts" setup>
import type { RecipientGift } from '@consumer/types/serializers'

import { useMotion } from '@vueuse/motion'

import { sleep } from '@shared/timers'
import { isDesktop } from '@consumer/utils/breakpoints'
import { setupRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import { isTest } from '@shared/environment'
import AmountCounter from './OpenGift/AmountCounter.vue'
import GiftGuide from './OpenGift/GiftGuide.vue'
import GiftTips from './OpenGift/GiftTips.vue'
import Stars from './OpenGift/Stars.vue'
import RedemptionOptions from './OpenGift/RedemptionOptions.vue'
import AnimateCoins from './OpenGift/AnimateCoins.vue'
import GiftCollage from './OpenGift/GiftCollage.vue'
import ThankYouCollage from './OpenGift/ThankYouCollage.vue'

const {
  gift,
} = defineProps<{
  gift: RecipientGift
  demo?: boolean
}>()

const animationSpeed = isTest ? 1 : 750

setupRecipientLanding(() => gift)

const canTransition = ref(true)
const enableTransition = () => canTransition.value = true
const disableTransition = () => canTransition.value = false

const giftCollage = ref<InstanceType<typeof GiftCollage>>()
const giftCollageMotion = useMotion(giftCollage, {
  initial: {
    opacity: 1,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    transition: { duration: 0 },
  },
  moveUp: {
    opacity: 1,
    left: '50%',
    top: '50%',
    transform: isDesktop.value
      ? 'translate(-50%, calc(-50% - 60px)) scale(1)'
      : 'translate(-50%, calc(-50% - 110px)) scale(0.8)',
    transitionProperty: 'transform, opacity',
    transitionBehavior: 'smooth',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: `${animationSpeed}ms`,
    transition: { ease: 'easeInOut' },
  },
  unskew: {
    opacity: 1,
    transform: 'translate(-50%, -50%) rotate(0deg) scale(0.9) perspective(700px) rotateY(89deg)',
    transitionProperty: 'transform, opacity',
    transitionBehavior: 'smooth',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: `${animationSpeed}ms`,
    transition: { ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    transform: 'translate(-50%, -50%) rotate(0deg) scale(0.9) perspective(700px) rotateY(89deg)',
    transitionProperty: 'transform, opacity',
    transitionBehavior: 'smooth',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: `${animationSpeed}ms`,
    transition: {
      transform: { ease: 'easeInOut', delay: 100 },
      opacity: { ease: 'easeInOut', delay: 100 },
    },
  },
  unskewInstant: {
    opacity: 0,
    transitionProperty: 'transform, opacity',
    transitionBehavior: 'smooth',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: `${animationSpeed}ms`,
    transform: 'translate(-50%, -50%) rotate(0deg) scale(0.9) perspective(700px) rotateY(0deg)',
  },
  hideInstant: {
    opacity: 0,
    transition: { duration: 0 },
  },
})

const stars = ref<InstanceType<typeof Stars>>()
const starsMotion = useMotion(stars, {
  initial: {
    opacity: 1,
    x: 20,
    y: 40,
    scale: 0.5,
    rotate: -45,
    transition: { ease: 'easeInOut', duration: 0 },
  },
  reveal: {
    opacity: 1,
    x: 20,
    y: 40,
    scale: 1,
    rotate: 0,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
  moveLeft: {
    opacity: 1,
    x: 0,
    y: 20,
    scale: 1,
    rotate: 0,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    x: 0,
    y: 20,
    scale: 1,
    rotate: 0,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
})

const giftTips = ref<InstanceType<typeof GiftTips>>()
const giftTipsMotion = useMotion(giftTips, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  show: {
    opacity: 1,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
})

const giftGuide = ref<InstanceType<typeof GiftGuide>>()
const giftGuideMotion = useMotion(giftGuide, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  show: {
    opacity: 1,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
})

const amountCounter = ref<InstanceType<typeof AmountCounter>>()
const amountCounterMotion = useMotion(amountCounter as any, {
  initial: {
    opacity: 1,
    left: '50%',
    top: 230,
    transform: 'translateX(-50%)',
    transition: { duration: 0 },
  },
  moveUp: {
    opacity: 1,
    left: '50%',
    top: isDesktop.value ? 80 : 50,
    transform: isDesktop.value
      ? 'translateX(-50%)'
      : 'translateX(-50%)',
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
  scaleDown: {
    opacity: 1,
    left: '50%',
    top: isDesktop.value ? 60 : 50,
    'transform-origin': 'top',
    transform: isDesktop.value
      ? 'translateX(-50%) scale(.7)'
      : 'translateX(-50%) scale(.7)',
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    left: '50%',
    top: 200,
    transform: 'translateX(-50%)',
    transition: { duration: animationSpeed, ease: 'easeInOut' },
  },
})

const redemptionOptions = ref<InstanceType<typeof RedemptionOptions>>()
const redemptionOptionsMotion = useMotion(redemptionOptions, {
  initial: {
    opacity: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    transition: { ease: 'easeInOut' },
  },
})

const animateCoins = ref<InstanceType<typeof AnimateCoins>>()
const animateCoinsMotion = useMotion(animateCoins as any, {
  initial: {
    opacity: 0,
    left: '50%',
    top: isDesktop.value ? '80px' : '40px',
    transform: 'translateX(-50%) scale(2.5)',
    transition: { duration: 0 },
  },
  show: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
    transition: { ease: 'easeInOut' },
  },
})

const thankYouCollage = ref<InstanceType<typeof ThankYouCollage>>()
const thankYouCollageMotion = useMotion(thankYouCollage, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
})

const goTo = (
  step:
  'ENVELOPE' |
  'GREETING_CARD_REVEAL' |
  'GREETING_CARD_OPEN' |
  'PHOTO_SLIDE_0' |
  'PHOTO_SLIDE_1' |
  'PHOTO_SLIDE_2' |
  'VOICE_MEMO' |
  'GIFTLY_REVEAL' |
  'GIFTLY_CENTER' |
  'GIFTLY_REDEMPTION' |
  'THANK_YOU',
) => {
  return new Promise<void>((resolve) => {
    disableTransition()

    giftTipsMotion.apply('initial')
    giftGuideMotion.apply('initial')
    giftCollageMotion.apply('initial')
    redemptionOptionsMotion.apply('initial')
    redemptionOptions.value?.toggleBankOption(false)
    animateCoinsMotion.apply('initial')
    animateCoins.value?.reset()
    amountCounterMotion.apply('initial')
    thankYouCollageMotion.apply('initial')
    thankYouCollage.value?.reset()

    switch (step) {
      case 'ENVELOPE':
        Promise.allSettled([
          starsMotion.apply('initial')?.then(() => starsMotion.apply('reveal')),
          amountCounterMotion.apply('initial'),
          amountCounter.value?.reset(),
          giftCollage.value?.initialize(),
        ]).then(() => {
          enableTransition()
          resolve()
        })
        break

      case 'GREETING_CARD_REVEAL':
        Promise.allSettled([
          starsMotion.apply('moveLeft'),
          amountCounterMotion.apply('initial'),
          amountCounter.value?.reset(),
          giftCollage.value?.unpack(),
        ]).then(() => {
          enableTransition()
          resolve()
        })
        break

      case 'GREETING_CARD_OPEN':
        Promise.allSettled([
          starsMotion.apply('hide'),
          amountCounterMotion.apply('initial'),
          amountCounter.value?.reset(),
          giftCollage.value?.openGreetingCard(),
        ])?.then(() => {
          enableTransition()
          resolve()
        })
        break

      case 'VOICE_MEMO':
        Promise.allSettled([
          starsMotion.apply('hide'),
          amountCounterMotion.apply('initial'),
          amountCounter.value?.reset(),
          giftCollage.value?.showVoiceMemo(),
        ]).then(() => {
          enableTransition()
          resolve()
        })
        break

      case 'PHOTO_SLIDE_0':
      case 'PHOTO_SLIDE_1':
      case 'PHOTO_SLIDE_2': {
        const slideIndex = Number(step.split('_')[2])
        Promise.allSettled([
          starsMotion.apply('hide'),
          amountCounterMotion.apply('initial'),
          amountCounter.value?.reset(),
          giftCollage.value?.showPhoto(slideIndex),
        ]).then(() => {
          enableTransition()
          resolve()
        })
        break
      }

      case 'GIFTLY_REVEAL':
        Promise.allSettled([
          starsMotion.apply('hide'),
          giftCollage.value?.revealGiftly(),
        ]).then(() => {
          giftTipsMotion.apply('show')
          giftGuideMotion.apply('show')
          amountCounterMotion.apply('moveUp')
          amountCounter.value?.start().then(() => {
            enableTransition()
            resolve()
          })
        })
        break

      case 'GIFTLY_CENTER':
        Promise.allSettled([
          starsMotion.apply('hide'),
          giftCollage.value?.align(),
        ]).then(() => {
          enableTransition()
          resolve()
        })
        break

      case 'GIFTLY_REDEMPTION':
        Promise.allSettled([
          starsMotion.apply('hide'),
          giftTipsMotion.apply('hide'),
          giftGuideMotion.apply('hide'),
          amountCounterMotion.apply('scaleDown'),
          giftCollageMotion.apply('moveUp'),
        ]).then(() => {
          Promise.allSettled([
            animateCoinsMotion.apply('show'),
            redemptionOptionsMotion.apply('reveal'),
          ]).then(async () => {
            redemptionOptions.value?.toggleBankOption(true)
            giftCollageMotion.apply('hide')?.then(() => {
              sleep(animationSpeed).then(() =>
                giftCollageMotion.apply('unskewInstant'),
              )
            })
            animateCoins.value?.reset()
            animateCoins.value?.play()
            await sleep(3000)
            animateCoinsMotion.apply('hide')
            enableTransition()
            resolve()
          })
        })
        break

      case 'THANK_YOU':
        amountCounterMotion.apply('hide')
        redemptionOptionsMotion.apply('hide')
        giftCollageMotion.apply('hideInstant')
        thankYouCollageMotion.apply('reveal')?.then(() => {
          thankYouCollage.value?.play()
          enableTransition()
          resolve()
        })
        break
    }
  })
}

defineExpose({
  goTo,
  canTransition,
})

</script>

<template>
  <div
    id="open-gift"
    class="relative"
    style="height: 470px; width: 400px;"
    :class="{
      'cursor-wait pointer-events-none': !canTransition,
      'cursor-pointer': canTransition,
    }"
  >
    <Stars
      id="stars"
      ref="stars"
      class="cursor-pointer absolute"
    />

    <AmountCounter
      id="amount-counter"
      ref="amountCounter"
      class="absolute transition-transform origin-top duration-500"
    />

    <AnimateCoins
      id="animate-coins"
      ref="animateCoins"
      class="absolute"
    />

    <GiftTips
      v-if="demo"
      id="gift-tips"
      ref="giftTips"
      class="absolute cursor-pointer -left-5 top-[70px] lg:-left-[120px] lg:top-[140px]"
      :style="{
        scale: isDesktop ? 1 : 0.8,
      }"
    />

    <GiftCollage
      id="gift-collage"
      ref="giftCollage"
      class="absolute"
      :animationSpeed="animationSpeed"
    />

    <GiftGuide
      v-if="demo"
      id="gift-guide"
      ref="giftGuide"
      class="absolute pointer-events-none -right-5 bottom-[-40px] lg:left-[240px] lg:top-[120px]"
      :style="{
        scale: isDesktop ? 1 : 0.8,
      }"
    />
    <RedemptionOptions
      id="redemption-options"
      ref="redemptionOptions"
      class="absolute z-2 w-full px-6 lg:w-auto lg:left-1/2 bottom-0 lg:bottom-[60px] duration-0
      pointer-events-none"
    />

    <ThankYouCollage
      id="thank-you-collage"
      ref="thankYouCollage"
      class="pointer-events-none"
    />
  </div>
</template>
